import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import TiptapEditor from "../../components/ui/TiptapEditor";
import { FileText, Award, BookOpen, Target, CheckCircle } from "lucide-react";
import { ProjectData, Difficulty } from "../../api/createProjectApi";
import {
  useResourceProfileByIdQuery,
  useResourceProfilesQuery,
} from "../../api/sandboxManagementApi";
import {
  getProjectById,
  getTemplateById,
} from "../../api/projectManagementApi";
import { ProjectDetailsStepProps } from "./types";
import {
  TagInput,
  TimeDurationInputs,
  GenericMultiSelect,
} from "../../components/CreateProject";

export const ProjectDetailsStep = React.memo<ProjectDetailsStepProps>(
  ({
    projectData,
    setProjectData,
    courses,
    coursesLoading,
    courseStaff,
    staffLoading,
    staffError,
    temporaryProjectId,
    setCurrentStep,
  }) => {
    const [formatted, setFormatted] = useState("");
    const [searchParams] = useSearchParams();
    const mode = searchParams.get("mode");
    const action = searchParams.get("action");
    const { projectId } = useParams();
    const resolvedId = projectId || temporaryProjectId;

    // Fetch resource profiles dropdown
    const { data: resourceProfiles, isLoading: resourceProfilesLoading } =
      useResourceProfilesQuery();

    // Project details query (non-template mode)
    const projectQuery = useQuery({
      queryKey: ["project-details", resolvedId],
      queryFn: () => getProjectById(resolvedId!),
      enabled: !!resolvedId && mode !== "template",
    });

    // Template details query (template edit mode) - UNIQUE KEY
    const templateQuery = useQuery({
      queryKey: ["template-details", resolvedId],
      queryFn: () => getTemplateById(resolvedId!),
      enabled: !!resolvedId && mode === "template" && action === "edit",
    });

    const {
      data: projectDataQuery,
      isLoading: projectLoading,
      error: projectError,
    } = projectQuery;
    const {
      data: templateData,
      isLoading: templateLoading,
      error: templateError,
    } = templateQuery;

    useEffect(() => {
      // Template edit mode - populate from template.template_name (API structure: {template, project})
      if (
        (templateData as any)?.template &&
        action === "edit" &&
        mode === "template"
      ) {
        if ((templateData as any)?.project) {
          const templateInfo = (templateData as any).template;
          const projectInfo = (templateData as any).project;

          setProjectData((prev) => ({
            ...prev,
            projectId: projectInfo.id, // Store project ID for saves
            title:
              templateInfo.template_name || projectInfo.title || prev.title,
            description:
              templateInfo.template_description ||
              projectInfo.description ||
              prev.description,
            estimatedHours:
              templateInfo.estimated_hours?.toString() ||
              projectInfo.estimated_hours?.toString() ||
              prev.estimatedHours,
            dueDate: projectInfo.due_date || prev.dueDate,
            resource_profile_id:
              projectInfo.resource_profile_id?.toString() ||
              prev.resource_profile_id,
            projectType:
              (projectInfo.type as "group" | "individual") || prev.projectType,
            difficulty_level:
              templateInfo.difficulty_level ||
              (projectInfo.difficulty_level as Difficulty) ||
              prev.difficulty_level,
            totalPoints:
              templateInfo.total_points ||
              projectInfo.total_points ||
              prev.totalPoints,
            tags: templateInfo.tags || projectInfo.tags || prev.tags,
            project_overview:
              projectInfo.project_overview || prev.project_overview,
            learning_objectives:
              templateInfo.learning_objectives ||
              projectInfo.learning_objectives ||
              prev.learning_objectives,
            prerequisites:
              templateInfo.prerequisites ||
              projectInfo.prerequisites ||
              prev.prerequisites,
          }));
        }
      }
    }, [templateData, action, mode, setProjectData]);

    useEffect(() => {
      // Regular project edit mode - use renamed projectDataQuery
      if (projectDataQuery && mode === "edit") {
        setProjectData((prev) => ({
          ...prev,
          id: projectDataQuery.id,
          projectId: projectDataQuery.projectId || projectDataQuery.id,
          title: projectDataQuery.title,
          description: projectDataQuery.description || "",
          difficulty_level: projectDataQuery.difficulty_level as Difficulty,
          totalPoints:
            projectDataQuery.total_points || projectDataQuery.totalPoints,
          estimatedHours: projectDataQuery.estimated_hours?.toString() || "8",
          dueDate: projectDataQuery.due_date || "",
          sandbox_time_duration:
            projectDataQuery.sandbox_time_duration ||
            prev.sandbox_time_duration ||
            "",
          tags: projectDataQuery.tags || [],
          project_overview: projectDataQuery.project_overview || "",
          learning_objectives: projectDataQuery.learning_objectives || "",
          prerequisites: projectDataQuery.prerequisites || "",
          instructions:
            projectDataQuery.instructions || prev.instructions || "",
          datasets: prev.datasets && prev.datasets.length > 0 ? prev.datasets : [],
          dataset_s3_url: projectDataQuery.dataset_s3_url,
          selectedDatasetId: undefined,
          selectedDatasetIds:
            projectDataQuery.linkedDatasets?.map((d) => d.id) || [],
          originalLinkedDatasetIds:
            projectDataQuery.linkedDatasets?.map((d) => d.id) || [],
          filesToDelete: prev.filesToDelete || [],
          gradingRubric: projectDataQuery.rubrics || [],
          resource_profile_id:
            typeof projectDataQuery.resourceProfile === "string"
              ? projectDataQuery.resourceProfile
              : projectDataQuery.resourceProfile?.id?.toString() || "",
        }));
      }
    }, [projectDataQuery, mode, setProjectData]);

    // Fetch resource profile details when resource_profile_id is set
    const resourceProfileId =
      projectData?.resource_profile_id &&
      projectData?.resource_profile_id !== "__none"
        ? String(projectData.resource_profile_id)
        : undefined;

    const {
      data: resourceProfile,
      isLoading: resourceProfileLoading,
      error: resourceProfileError,
    } = useResourceProfileByIdQuery(resourceProfileId, {
      enabled: !!resourceProfileId,
      staleTime: 0,
      gcTime: 5 * 60 * 1000,
    });

    return (
      <div className="space-y-6">
        {projectLoading || templateLoading ? (
          <Card className="h-[50vh]">
            <p className="flex justify-center items-center h-full text-bits-grey-600">
              Fetching Project/Template Details...
            </p>
          </Card>
        ) : (
          <>
            {/* Basic Information Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-bits-blue" />
                  <CardTitle>Basic Information</CardTitle>
                </div>
                <p className="text-sm text-gray-600">
                  Configure the basic project information and requirements
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="title">Project Name *</Label>

                    <Input
                      id="title"
                      placeholder="Enter project name"
                      value={projectData.title}
                      className="bg-bits-grey-100"
                      onChange={(e) => {
                        setProjectData((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }));
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="courseId">Select Course</Label>
                    <Select
                      value={String(projectData.courseId ?? "")}
                      disabled={true}
                      onValueChange={(value) => {
                        setProjectData((prev) => ({
                          ...prev,
                          courseId: value === "__none" ? "" : value,
                        }));
                      }}
                    >
                      <SelectTrigger className="bg-bits-grey-100">
                        <SelectValue placeholder="-- Select Course --" />
                      </SelectTrigger>

                      <SelectContent>
                        {coursesLoading ? (
                          <SelectItem value="loading" key="loading" disabled>
                            Loading courses...
                          </SelectItem>
                        ) : (
                          courses?.map((course) => (
                            <SelectItem
                              key={course.id}
                              value={String(course.id)}
                            >
                              {course.name}{" "}
                              {course.code ? `(${course.code})` : ""}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Project Description *</Label>
                  <TiptapEditor
                    id="description"
                    placeholder="Brief description of the project"
                    className="bg-bits-grey-100 text-bits-grey-600"
                    value={projectData.description || ""}
                    onChange={(content) =>
                      setProjectData((prev) => ({
                        ...prev,
                        description: content,
                      }))
                    }
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="instructor">Instructor</Label>
                    <GenericMultiSelect
                      projectData={projectData}
                      setProjectData={setProjectData}
                      valueToCheck="instructors"
                      items={courseStaff?.instructors ?? []}
                      label="Instructors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="teachingAssId">Teaching Assistant</Label>
                    <GenericMultiSelect
                      projectData={projectData}
                      setProjectData={setProjectData}
                      valueToCheck="teachingAssistants"
                      items={courseStaff?.tas ?? []}
                      label="TAs"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label>Project Type *</Label>
                    <RadioGroup
                      value={projectData.projectType}
                      onValueChange={(value: "group" | "individual") =>
                        setProjectData((prev) => ({
                          ...prev,
                          projectType: value,
                        }))
                      }
                      className="flex space-x-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="group" id="group" />
                        <Label htmlFor="group">Group Project</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="individual" id="individual" />
                        <Label htmlFor="individual">Individual Project</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="difficulty">Difficulty Level *</Label>
                    <Select
                      value={projectData.difficulty_level || ""}
                      onValueChange={(value: Difficulty) =>
                        setProjectData((prev) => ({
                          ...prev,
                          difficulty_level: value,
                        }))
                      }
                    >
                      <SelectTrigger className="text-bits-grey-500 bg-bits-grey-100 capitalize">
                        <SelectValue placeholder="-- Select Difficulty Level --" />
                      </SelectTrigger>

                      <SelectContent className="capitalize">
                        <SelectItem value="beginner">beginner</SelectItem>
                        <SelectItem value="intermediate">
                          intermediate
                        </SelectItem>
                        <SelectItem value="advanced">advanced</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Project Metadata Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <Award className="h-5 w-5 text-bits-blue" />
                  <CardTitle>Project Metadata</CardTitle>
                </div>
                <p className="text-sm text-gray-600">
                  Additional project information for tracking and organization
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="resource_profile_id">
                      Select Resource Profile
                    </Label>
                    <Select
                      value={String(projectData.resource_profile_id ?? "")}
                      onValueChange={(value) => {
                        setProjectData((prev) => ({
                          ...prev,
                          resource_profile_id: value === "__none" ? "" : value,
                        }));
                      }}
                    >
                      <SelectTrigger className="bg-bits-grey-100">
                        <SelectValue placeholder="-- Select Resource Profile --" />
                      </SelectTrigger>

                      <SelectContent className="max-h-[300px] overflow-y-auto">
                        {resourceProfilesLoading ? (
                          <SelectItem value="loading" key="loading" disabled>
                            Loading Resource Profiles...
                          </SelectItem>
                        ) : (
                          <>
                            <SelectItem value="__none">
                              -- Select Resource Profile --
                            </SelectItem>
                            {resourceProfiles?.profiles?.map((profile) => (
                              <SelectItem
                                key={profile.id}
                                value={String(profile.id)}
                              >
                                {profile.name}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="total_points">Total Score *</Label>
                    <Input
                      id="totalPoints"
                      type="text"
                      placeholder="Ex.100"
                      value={projectData.totalPoints || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val)) {
                          setProjectData((prev) => ({
                            ...prev,
                            totalPoints: val === "" ? 0 : Number(val),
                          }));
                        }
                      }}
                      className="text-bits-grey-500 bg-bits-grey-100"
                    />
                    <p className="text-xs text-gray-500">
                      Maximum points students can earn for this project
                    </p>
                  </div>
                </div>

                {/* Resource Profile Details */}
                {resourceProfile && (
                  <div className="p-5 bg-yellow-50 border border-yellow-100 rounded-xl shadow-sm space-y-3">
                    <h2 className="text-lg font-semibold text-gray-800">
                      {resourceProfile?.name}
                    </h2>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-700">
                      <p>
                        <strong>Resource Type:</strong>{" "}
                        {resourceProfile?.resource_type}
                      </p>
                      <p>
                        <strong>CPU Cores:</strong> {resourceProfile?.cpu_cores}
                      </p>
                      <p>
                        <strong>RAM (GB):</strong> {resourceProfile?.ram_gb}
                      </p>
                      <p>
                        <strong>Disk (GB):</strong> {resourceProfile?.disk_gb}
                      </p>
                      <p>
                        <strong>Build Status:</strong>{" "}
                        {resourceProfile?.build_status}
                      </p>

                      {resourceProfile?.packages?.length > 0 && (
                        <p className="col-span-2">
                          <strong>Packages:</strong>{" "}
                          {resourceProfile?.packages.join(", ")}
                        </p>
                      )}
                      {resourceProfile?.custom_packages && (
                        <p className="col-span-2">
                          <strong>Custom Packages:</strong>{" "}
                          {resourceProfile?.custom_packages}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="estimatedHours">
                      Estimated Time(hours) *
                    </Label>
                    <Input
                      className="text-bits-grey-500 bg-bits-grey-100"
                      type="text"
                      id="estimatedHours"
                      placeholder="Enter hours"
                      value={projectData?.estimatedHours || ""}
                      onChange={(e) => {
                        const value = e.target.value;

                        if (value === "") {
                          setProjectData((prev) => ({
                            ...prev,
                            estimatedHours: "",
                          }));
                          return;
                        }

                        const sanitizedValue = value.replace(/[^0-9.]/g, "");
                        const parts = sanitizedValue.split(".");
                        const validValue =
                          parts.length > 2
                            ? parts[0] + "." + parts.slice(1).join("")
                            : sanitizedValue;

                        const numValue = parseFloat(validValue);

                        if (!isNaN(numValue) && numValue > 0) {
                          setProjectData((prev) => ({
                            ...prev,
                            estimatedHours: numValue.toString(),
                          }));
                        } else if (validValue !== "") {
                          setProjectData((prev) => ({
                            ...prev,
                            estimatedHours: validValue,
                          }));
                        }
                      }}
                    />
                    <p className="text-xs text-gray-500">
                      Expected time for students to complete this project
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sandbox_time_duration">
                      Sandbox available duration (HH:MM)
                    </Label>
                    <TimeDurationInputs
                      value={projectData?.sandbox_time_duration}
                      onChange={(f) => {
                        setFormatted(f);
                        setProjectData((prev) => ({
                          ...prev,
                          sandbox_time_duration: f,
                        }));
                      }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dueDate">Project End Date *</Label>
                  <Input
                    className="text-bits-grey-500 bg-bits-grey-100"
                    type="date"
                    id="dueDate"
                    placeholder="Enter hours"
                    min={(() => {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      return tomorrow.toISOString().split("T")[0];
                    })()}
                    value={
                      projectData.dueDate
                        ? projectData.dueDate.split("T")[0]
                        : ""
                    }
                    onChange={(e) => {
                      const newDue = e.target.value;
                      if (!newDue) {
                        setProjectData((prev) => ({ ...prev, dueDate: "" }));
                        return;
                      }

                      const newDueDate = new Date(newDue);
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      tomorrow.setHours(0, 0, 0, 0);

                      if (newDueDate < tomorrow) {
                        toast.error(
                          "Project Due Date must be at least tomorrow",
                        );
                        return;
                      }

                      setProjectData((prev) => ({
                        ...prev,
                        dueDate: newDueDate.toISOString(),
                      }));
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tags">Project Tags</Label>
                  <TagInput
                    tags={projectData.tags}
                    setTags={(tags) =>
                      setProjectData((prev) => ({ ...prev, tags }))
                    }
                    placeholder="Add tags to categorize this project (e.g., machine-learning, python, beginner)"
                  />
                  <p className="text-xs text-gray-500">
                    Tags help students find relevant projects and improve
                    searchability
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Project Overview Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <BookOpen className="h-5 w-5 text-bits-blue" />
                  <CardTitle>Project Overview</CardTitle>
                </div>
                <p className="text-sm text-gray-600">
                  Provide a comprehensive overview of the project scope and
                  context
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {/* <Label htmlFor="project_overview">Project Overview</Label> */}
                  <TiptapEditor
                    id="project_overview"
                    placeholder="Provide a detailed overview of the project including its purpose, scope, methodology, and expected outcomes. This should give students a comprehensive understanding of what they will be working on and why it's important."
                    className="min-h-[120px] text-bits-grey-600 bg-bits-grey-100"
                    value={projectData.project_overview}
                    onChange={(content) =>
                      setProjectData((prev) => ({
                        ...prev,
                        project_overview: content,
                      }))
                    }
                  />
                  <p className="text-xs text-gray-500">
                    This overview will help students understand the broader
                    context and significance of the project.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Learning Objectives Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <Target className="h-5 w-5 text-bits-blue" />
                  <CardTitle>Learning Objectives</CardTitle>
                </div>
                <p className="text-sm text-gray-600">
                  Define what students will learn and achieve through this
                  project
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {/* <Label htmlFor="learning_objectives">
                    Learning Objectives
                  </Label> */}
                  <TiptapEditor
                    id="learning_objectives"
                    placeholder={`List the specific learning objectives for this project. What skills, knowledge, or competencies will students gain? 

Example:
• Understand fundamental concepts of machine learning algorithms
• Apply data preprocessing techniques to real-world datasets  
• Develop proficiency in Python libraries (pandas, scikit-learn, matplotlib)
• Create and evaluate predictive models
• Communicate findings through data visualization and reporting`}
                    className="h-full bg-bits-grey-100 text-bits-grey-600"
                    value={projectData.learning_objectives}
                    onChange={(content) =>
                      setProjectData((prev) => ({
                        ...prev,
                        learning_objectives: content,
                      }))
                    }
                  />
                  <p className="text-xs text-gray-500">
                    Use bullet points or numbered lists to clearly define what
                    students will learn.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Prerequisites Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-bits-blue" />
                  <CardTitle>Prerequisites & Requirements</CardTitle>
                </div>
                <p className="text-sm text-gray-600">
                  Specify the knowledge, skills, and tools students need before
                  starting
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {/* <Label htmlFor="prerequisites">
                    Prerequisites & Requirements
                  </Label> */}
                  <TiptapEditor
                    id="prerequisites"
                    placeholder={`List the prerequisites and requirements for this project:

Technical Prerequisites:
• Basic Python programming knowledge
• Familiarity with Jupyter notebooks
• Understanding of basic statistics

Required Tools/Software:
• Python 3.8+
• Jupyter Notebook or Google Colab
• Required libraries will be provided

Recommended Background:
• Introduction to Data Science course
• Basic mathematics (algebra, statistics)`}
                    className="min-h-[140px] bg-bits-grey-100 text-bits-grey-600"
                    value={projectData.prerequisites}
                    onChange={(content) =>
                      setProjectData((prev) => ({
                        ...prev,
                        prerequisites: content,
                      }))
                    }
                  />
                  <p className="text-xs text-gray-500">
                    Include technical skills, software requirements, and any
                    recommended background knowledge.
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  },
);

ProjectDetailsStep.displayName = "ProjectDetailsStep";
